import { type FormEvent, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  buildHealthObservationOperation,
  buildMortalityOperation,
  buildSiphonOperation,
  buildTreatmentOperation,
  buildWaterChangeOperation,
  buildWaterMeasurementOperation,
  type TreatmentRoute,
} from "../operations/workerOperations";
import { loadHallPatrolTankSnapshots } from "../qr/liveTankSnapshots";
import type { HallPatrolTankSnapshot } from "../qr/hallPatrol";
import type { WorkerOperationKind } from "../qr/hallPatrolM2Bridge";
import { readHallPatrolTankCache, writeHallPatrolTankCache } from "../qr/tankSnapshotCache";
import { getOrCreateDeviceId } from "../sync/deviceIdentity";
import { persistAndSyncOperation } from "../sync/operationRuntime";
import type { OfflineOperation, OutboxSummary } from "../sync/types";
import { supabase } from "../auth/supabaseClient";
import { ConfirmationSheet } from "./ConfirmationSheet";

type OperationKind = WorkerOperationKind;

interface WorkerHallOperationsPanelProps {
  farmId: string;
  userId: string;
  initialTankId?: string;
  initialOperation?: WorkerOperationKind;
  renderBreedingPanel?: (tankId: string) => ReactNode;
  onSyncSummary?: (summary: OutboxSummary) => void;
}

interface PendingHallOperation {
  operation: OfflineOperation;
  parts: string[];
}

const OPERATION_META: Readonly<
  Record<OperationKind, { label: string; icon: string; permission: string }>
> = {
  water_measurement: { label: "اندازه‌گیری آب", icon: "💧", permission: "water.record" },
  water_change: { label: "تعویض آب", icon: "🚿", permission: "water.record" },
  siphon: { label: "سیفون", icon: "🧹", permission: "siphon.record" },
  health_observation: { label: "سلامت", icon: "🩺", permission: "health.record" },
  mortality: { label: "تلفات", icon: "⚠️", permission: "health.record" },
  treatment: { label: "درمان", icon: "🧪", permission: "treatment.record" },
};

function optionalNumber(data: FormData, key: string): number | undefined {
  const raw = String(data.get(key) ?? "").trim();
  if (!raw) return undefined;
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
}

function textValue(data: FormData, key: string): string | undefined {
  const value = String(data.get(key) ?? "").trim();
  return value || undefined;
}

function checked(data: FormData, key: string): boolean {
  return data.get(key) === "on";
}

function statusMessage(status: string): string {
  switch (status) {
    case "synced":
      return "ثبت شد و با سرور همگام شد.";
    case "pending":
      return "آفلاین ثبت شد و در صف همگام‌سازی قرار گرفت.";
    case "retryable_error":
      return "ثبت محلی حفظ شد؛ ارسال دوباره انجام خواهد شد.";
    case "terminal_conflict":
      return "ثبت محلی حفظ شد اما نیاز به بررسی تعارض دارد.";
    default:
      return "عملیات ثبت شد.";
  }
}

export function WorkerHallOperationsPanel({
  farmId,
  userId,
  initialTankId,
  initialOperation,
  renderBreedingPanel,
  onSyncSummary,
}: WorkerHallOperationsPanelProps) {
  const formRef = useRef<HTMLFormElement | null>(null);
  const [tanks, setTanks] = useState<HallPatrolTankSnapshot[]>([]);
  const [source, setSource] = useState<"cache" | "live" | "empty">("empty");
  const [selectedTankId, setSelectedTankId] = useState(initialTankId ?? "");
  const [operation, setOperation] = useState<OperationKind>(initialOperation ?? "water_change");
  const [pending, setPending] = useState<PendingHallOperation>();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();
  const [breedingOpen, setBreedingOpen] = useState(false);

  useEffect(() => {
    if (initialTankId) setSelectedTankId(initialTankId);
  }, [initialTankId]);

  useEffect(() => {
    if (initialOperation) setOperation(initialOperation);
  }, [initialOperation]);

  useEffect(() => {
    let cancelled = false;

    void readHallPatrolTankCache(farmId).then((cached) => {
      if (cancelled || !cached?.length) return;
      setTanks(cached.filter((tank) => tank.active));
      setSource("cache");
    });

    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      return () => {
        cancelled = true;
      };
    }

    void loadHallPatrolTankSnapshots(supabase, { farmId, userId })
      .then(async (live) => {
        if (cancelled) return;
        const active = live.filter((tank) => tank.active);
        setTanks(active);
        setSource("live");
        await writeHallPatrolTankCache(farmId, live).catch(() => undefined);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [farmId, userId]);

  const selectedTank = useMemo(
    () => tanks.find((tank) => tank.tankId === selectedTankId),
    [selectedTankId, tanks],
  );

  const permissions = useMemo(() => new Set(selectedTank?.permissions ?? []), [selectedTank]);
  const visibleOperations = useMemo(() => {
    if (!selectedTank) return [];

    return (Object.keys(OPERATION_META) as OperationKind[]).filter((kind) => {
      if (!permissions.has(OPERATION_META[kind].permission) && source !== "empty") return false;
      if (kind === "mortality" && !selectedTank.stock) return false;
      return true;
    });
  }, [permissions, selectedTank, source]);
  const breedingRelevant = Boolean(
    selectedTank &&
      renderBreedingPanel &&
      permissions.has("breeding.record") &&
      (Boolean(selectedTank.breeding) || /^[BP]/i.test(selectedTank.tankCode)),
  );

  useEffect(() => {
    if (!visibleOperations.includes(operation)) {
      setOperation(visibleOperations[0] ?? "water_change");
    }
  }, [operation, visibleOperations]);

  function requestConfirmation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage(undefined);
    setError(undefined);

    if (!selectedTank) {
      setError("ابتدا تانک را انتخاب کنید.");
      return;
    }

    const data = new FormData(event.currentTarget);
    const note = textValue(data, "notes");
    const context = {
      actorId: userId,
      farmId,
      deviceId: getOrCreateDeviceId(),
      tankId: selectedTank.tankId,
      performedAt: new Date().toISOString(),
      ...(note ? { notes: note } : {}),
    };

    try {
      let queued: OfflineOperation;
      const parts = [`تانک ${selectedTank.tankCode}`, OPERATION_META[operation].label];

      switch (operation) {
        case "water_measurement": {
          const temperatureC = optionalNumber(data, "temperatureC");
          const ph = optionalNumber(data, "ph");
          const tdsPpm = optionalNumber(data, "tdsPpm");
          const ammoniaMgL = optionalNumber(data, "ammoniaMgL");
          const nitriteMgL = optionalNumber(data, "nitriteMgL");
          const nitrateMgL = optionalNumber(data, "nitrateMgL");
          const ghDgh = optionalNumber(data, "ghDgh");
          const khDkh = optionalNumber(data, "khDkh");
          const dissolvedOxygenMgL = optionalNumber(data, "dissolvedOxygenMgL");

          queued = buildWaterMeasurementOperation(context, {
            ...(temperatureC === undefined ? {} : { temperatureC }),
            ...(ph === undefined ? {} : { ph }),
            ...(tdsPpm === undefined ? {} : { tdsPpm }),
            ...(ammoniaMgL === undefined ? {} : { ammoniaMgL }),
            ...(nitriteMgL === undefined ? {} : { nitriteMgL }),
            ...(nitrateMgL === undefined ? {} : { nitrateMgL }),
            ...(ghDgh === undefined ? {} : { ghDgh }),
            ...(khDkh === undefined ? {} : { khDkh }),
            ...(dissolvedOxygenMgL === undefined ? {} : { dissolvedOxygenMgL }),
          });

          if (temperatureC !== undefined) parts.push(`دما ${temperatureC} درجه`);
          if (ph !== undefined) parts.push(`pH ${ph}`);
          if (tdsPpm !== undefined) parts.push(`TDS ${tdsPpm}`);
          break;
        }

        case "water_change": {
          const changedPercent = optionalNumber(data, "changedPercent");
          const changedVolumeL = optionalNumber(data, "changedVolumeL");
          const tdsBefore = optionalNumber(data, "tdsBefore");
          const tdsAfter = optionalNumber(data, "tdsAfter");
          const reason = textValue(data, "reason");

          queued = buildWaterChangeOperation(context, {
            ...(changedPercent === undefined ? {} : { changedPercent }),
            ...(changedVolumeL === undefined ? {} : { changedVolumeL }),
            ...(tdsBefore === undefined ? {} : { tdsBefore }),
            ...(tdsAfter === undefined ? {} : { tdsAfter }),
            ...(reason ? { reason } : {}),
          });

          if (changedPercent !== undefined) parts.push(`${changedPercent} درصد`);
          if (changedVolumeL !== undefined) parts.push(`${changedVolumeL} لیتر`);
          break;
        }

        case "siphon": {
          const mortalityCount = optionalNumber(data, "mortalityCount");
          const siphonPercent = optionalNumber(data, "siphonPercent");
          const removedWaterL = optionalNumber(data, "removedWaterL");
          const dirtLevelAfter = textValue(data, "dirtLevelAfter");

          queued = buildSiphonOperation(context, {
            dirtLevelBefore: String(data.get("dirtLevelBefore") ?? "medium") as
              | "clean"
              | "low"
              | "medium"
              | "high",
            ...(dirtLevelAfter
              ? {
                  dirtLevelAfter: dirtLevelAfter as "clean" | "low" | "medium" | "high",
                }
              : {}),
            ...(siphonPercent === undefined
              ? {}
              : { siphonPercent: siphonPercent as 10 | 20 | 30 | 50 | 90 | 100 }),
            leftoverFoodSeen: checked(data, "leftoverFoodSeen"),
            abnormalFecesSeen: checked(data, "abnormalFecesSeen"),
            deadFishSeen: checked(data, "deadFishSeen"),
            ...(removedWaterL === undefined ? {} : { removedWaterL }),
            ...(mortalityCount === undefined ? {} : { mortalityCount }),
            ...(selectedTank.stock
              ? {
                  stockGroupId: selectedTank.stock.stockGroupId,
                  stockVersion: selectedTank.stock.stockVersion,
                }
              : {}),
          });

          if (siphonPercent !== undefined) parts.push(`سیفون ${siphonPercent} درصد`);
          if (mortalityCount) parts.push(`تلفات مشاهده‌شده ${mortalityCount}`);
          break;
        }

        case "health_observation": {
          const overallStatus = String(data.get("overallStatus") ?? "watch") as
            | "good"
            | "watch"
            | "poor"
            | "critical";
          const swimmingStatus = textValue(data, "swimmingStatus");
          const appetiteStatus = textValue(data, "appetiteStatus");
          const appearanceStatus = textValue(data, "appearanceStatus");
          const fecesStatus = textValue(data, "fecesStatus");
          const respirationStatus = textValue(data, "respirationStatus");

          queued = buildHealthObservationOperation(context, {
            overallStatus,
            ...(swimmingStatus ? { swimmingStatus } : {}),
            ...(appetiteStatus ? { appetiteStatus } : {}),
            ...(appearanceStatus ? { appearanceStatus } : {}),
            ...(fecesStatus ? { fecesStatus } : {}),
            ...(respirationStatus ? { respirationStatus } : {}),
            ...(selectedTank.stock ? { stockGroupId: selectedTank.stock.stockGroupId } : {}),
          });
          parts.push(`وضعیت کلی: ${overallStatus}`);
          break;
        }

        case "mortality": {
          if (!selectedTank.stock) {
            throw new Error("برای این تانک گله فعال قابل ثبت تلفات پیدا نشد.");
          }

          const deadCount = optionalNumber(data, "deadCount") ?? 0;
          const cause = textValue(data, "cause");
          queued = buildMortalityOperation(context, {
            deadCount,
            stockGroupId: selectedTank.stock.stockGroupId,
            stockVersion: selectedTank.stock.stockVersion,
            ...(cause ? { cause } : {}),
          });
          parts.push(`تعداد ${deadCount}`);
          break;
        }

        case "treatment": {
          const medicineName = textValue(data, "medicineName") ?? "";
          const doseValue = optionalNumber(data, "doseValue");
          const doseUnit = textValue(data, "doseUnit");
          const durationDays = optionalNumber(data, "durationDays");
          const route = String(data.get("route") ?? "water") as TreatmentRoute;

          queued = buildTreatmentOperation(context, {
            medicineName,
            route,
            ...(doseValue === undefined ? {} : { doseValue }),
            ...(doseUnit ? { doseUnit } : {}),
            ...(durationDays === undefined ? {} : { durationDays }),
          });
          parts.push(`دارو: ${medicineName}`);
          if (doseValue !== undefined) {
            parts.push(`دوز: ${doseValue}${doseUnit ? ` ${doseUnit}` : ""}`);
          }
          break;
        }
      }

      setPending({ operation: queued, parts });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "اطلاعات عملیات معتبر نیست.");
    }
  }

  async function confirmPending() {
    if (!pending) return;

    try {
      setBusy(true);
      setError(undefined);
      const result = await persistAndSyncOperation(
        supabase,
        pending.operation,
        typeof navigator === "undefined" ? true : navigator.onLine !== false,
      );

      onSyncSummary?.(result.summary);
      setMessage(statusMessage(result.operation.status));
      setPending(undefined);
      formRef.current?.reset();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "ثبت عملیات انجام نشد.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="section-card" aria-labelledby="worker-ops-title">
      <div className="section-title-row">
        <div>
          <p className="section-kicker">عملیات سالن</p>
          <h2 id="worker-ops-title">ثبت سریع واقعی</h2>
        </div>
        <span className="muted-chip">
          {source === "live" ? "آنلاین" : source === "cache" ? "کش آفلاین" : "در انتظار"}
        </span>
      </div>

      <label className="operation-field">
        <span>تانک</span>
        <select
          value={selectedTankId}
          onChange={(event) => {
            setSelectedTankId(event.target.value);
            setBreedingOpen(false);
            setPending(undefined);
            setMessage(undefined);
            setError(undefined);
          }}
        >
          <option value="">انتخاب تانک</option>
          {tanks.map((tank) => (
            <option value={tank.tankId} key={tank.tankId}>
              {tank.tankCode}
            </option>
          ))}
        </select>
      </label>

      {selectedTank ? (
        <div className="quick-grid">
          {visibleOperations
            .filter((kind) => kind !== "mortality")
            .map((kind) => (
              <button
                type="button"
                className={
                  !breedingOpen && operation === kind ? "quick-button active" : "quick-button"
                }
                key={kind}
                onClick={() => {
                  setBreedingOpen(false);
                  setOperation(kind);
                  setPending(undefined);
                  setMessage(undefined);
                  setError(undefined);
                }}
              >
                <span aria-hidden="true">{OPERATION_META[kind].icon}</span>
                <strong>{OPERATION_META[kind].label}</strong>
              </button>
            ))}

          {breedingRelevant ? (
            <button
              type="button"
              className={breedingOpen ? "quick-button active" : "quick-button"}
              onClick={() => {
                setBreedingOpen(true);
                setPending(undefined);
                setMessage(undefined);
                setError(undefined);
              }}
            >
              <span aria-hidden="true">🐣</span>
              <strong>تکثیر</strong>
            </button>
          ) : null}

          {visibleOperations
            .filter((kind) => kind === "mortality")
            .map((kind) => (
              <button
                type="button"
                className={
                  !breedingOpen && operation === kind ? "quick-button active" : "quick-button"
                }
                key={kind}
                onClick={() => {
                  setBreedingOpen(false);
                  setOperation(kind);
                  setPending(undefined);
                  setMessage(undefined);
                  setError(undefined);
                }}
              >
                <span aria-hidden="true">{OPERATION_META[kind].icon}</span>
                <strong>{OPERATION_META[kind].label}</strong>
              </button>
            ))}
        </div>
      ) : (
        <p className="empty-state">ابتدا تانک را انتخاب کنید تا عملیات مرتبط نمایش داده شود.</p>
      )}

      {breedingOpen && breedingRelevant && selectedTank && renderBreedingPanel ? (
        <div className="breeding-inline-panel">{renderBreedingPanel(selectedTank.tankId)}</div>
      ) : null}

      {selectedTank && !breedingOpen ? (
        <form
          ref={formRef}
          className="operation-form"
          onSubmit={(event) => requestConfirmation(event)}
        >
          {operation === "water_measurement" ? (
            <div className="operation-field-grid">
              <label>
                دما °C
                <input name="temperatureC" inputMode="decimal" type="number" step="any" />
              </label>
              <label>
                pH
                <input name="ph" inputMode="decimal" type="number" step="any" />
              </label>
              <label>
                TDS ppm
                <input name="tdsPpm" inputMode="numeric" type="number" step="1" />
              </label>
              <label>
                آمونیاک mg/L
                <input name="ammoniaMgL" inputMode="decimal" type="number" step="any" />
              </label>
              <label>
                نیتریت mg/L
                <input name="nitriteMgL" inputMode="decimal" type="number" step="any" />
              </label>
              <label>
                نیترات mg/L
                <input name="nitrateMgL" inputMode="decimal" type="number" step="any" />
              </label>
              <label>
                GH
                <input name="ghDgh" inputMode="decimal" type="number" step="any" />
              </label>
              <label>
                KH
                <input name="khDkh" inputMode="decimal" type="number" step="any" />
              </label>
              <label>
                اکسیژن mg/L
                <input name="dissolvedOxygenMgL" inputMode="decimal" type="number" step="any" />
              </label>
            </div>
          ) : null}

          {operation === "water_change" ? (
            <div className="operation-field-grid">
              <label>
                درصد تعویض
                <select name="changedPercent" defaultValue="30">
                  {[10, 20, 30, 50, 90, 100].map((value) => (
                    <option value={value} key={value}>
                      {value}٪
                    </option>
                  ))}
                </select>
              </label>
              <label>
                حجم لیتر
                <input name="changedVolumeL" inputMode="decimal" type="number" step="any" />
              </label>
              <label>
                TDS قبل
                <input name="tdsBefore" inputMode="numeric" type="number" />
              </label>
              <label>
                TDS بعد
                <input name="tdsAfter" inputMode="numeric" type="number" />
              </label>
              <label>
                علت
                <input name="reason" />
              </label>
            </div>
          ) : null}

          {operation === "siphon" ? (
            <>
              <div className="operation-field-grid">
                <label>
                  کثیفی قبل
                  <select name="dirtLevelBefore" defaultValue="medium">
                    <option value="clean">تمیز</option>
                    <option value="low">کم</option>
                    <option value="medium">متوسط</option>
                    <option value="high">زیاد</option>
                  </select>
                </label>
                <label>
                  کثیفی بعد
                  <select name="dirtLevelAfter" defaultValue="low">
                    <option value="clean">تمیز</option>
                    <option value="low">کم</option>
                    <option value="medium">متوسط</option>
                    <option value="high">زیاد</option>
                  </select>
                </label>
                <label>
                  درصد سیفون
                  <select name="siphonPercent" defaultValue="30">
                    {[10, 20, 30, 50, 90, 100].map((value) => (
                      <option value={value} key={value}>
                        {value}٪
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  آب خارج‌شده (لیتر)
                  <input name="removedWaterL" type="number" step="any" />
                </label>
                {selectedTank?.stock ? (
                  <label>
                    تعداد تلفات مشاهده‌شده
                    <input name="mortalityCount" type="number" min="0" step="1" />
                  </label>
                ) : null}
              </div>
              <div className="operation-checks">
                <label>
                  <input name="leftoverFoodSeen" type="checkbox" /> غذای مانده
                </label>
                <label>
                  <input name="abnormalFecesSeen" type="checkbox" /> مدفوع غیرعادی
                </label>
                <label>
                  <input name="deadFishSeen" type="checkbox" /> ماهی مرده دیده شد
                </label>
              </div>
            </>
          ) : null}

          {operation === "health_observation" ? (
            <div className="operation-field-grid">
              <label>
                وضعیت کلی
                <select name="overallStatus" defaultValue="watch">
                  <option value="good">خوب</option>
                  <option value="watch">نیاز به توجه</option>
                  <option value="poor">ضعیف</option>
                  <option value="critical">بحرانی</option>
                </select>
              </label>
              <label>
                شنا
                <input name="swimmingStatus" />
              </label>
              <label>
                اشتها
                <input name="appetiteStatus" />
              </label>
              <label>
                ظاهر
                <input name="appearanceStatus" />
              </label>
              <label>
                مدفوع
                <input name="fecesStatus" />
              </label>
              <label>
                تنفس
                <input name="respirationStatus" />
              </label>
            </div>
          ) : null}

          {operation === "mortality" ? (
            <div className="operation-field-grid">
              <label>
                تعداد تلفات
                <input name="deadCount" type="number" min="1" step="1" required />
              </label>
              <label>
                علت احتمالی
                <input name="cause" />
              </label>
            </div>
          ) : null}

          {operation === "treatment" ? (
            <div className="operation-field-grid">
              <label>
                دارو
                <input name="medicineName" required />
              </label>
              <label>
                روش
                <select name="route" defaultValue="water">
                  <option value="water">داخل آب</option>
                  <option value="bath">حمام</option>
                  <option value="feed">غذا</option>
                  <option value="oral">خوراکی</option>
                  <option value="injection">تزریق</option>
                  <option value="topical">موضعی</option>
                  <option value="other">سایر</option>
                </select>
              </label>
              <label>
                دوز
                <input name="doseValue" type="number" step="any" />
              </label>
              <label>
                واحد دوز
                <input name="doseUnit" />
              </label>
              <label>
                مدت (روز)
                <input name="durationDays" type="number" min="1" step="1" />
              </label>
            </div>
          ) : null}

          <label className="operation-field">
            <span>یادداشت</span>
            <textarea name="notes" rows={2} />
          </label>

          {message ? (
            <p className="operation-success" aria-live="polite">
              {message}
            </p>
          ) : null}
          {error ? (
            <p className="settings-warning" aria-live="assertive">
              {error}
            </p>
          ) : null}

          <button type="submit" className="patrol-button" disabled={busy || !selectedTank}>
            <span aria-hidden="true">✓</span>
            <span>
              <strong>{busy ? "در حال ثبت…" : "ادامه و تأیید"}</strong>
              <small>قبل از ثبت نهایی خلاصه نمایش داده می‌شود</small>
            </span>
          </button>
        </form>
      ) : null}

      {pending ? (
        <ConfirmationSheet
          title="تأیید عملیات سالن"
          parts={pending.parts}
          speechEnabled
          fastConfirmEnabled
          onCancel={() => setPending(undefined)}
          onConfirm={() => void confirmPending()}
        />
      ) : null}
    </section>
  );
}
